import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, Badge, Button, ListGroup, ListGroupItem } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { teamErrorMessage } from './teamErrorMessage';
import { TeamRemoveMemberModal } from './TeamRemoveMemberModal';
import { Panel, PanelBody, PanelHead } from '../music24/components/primitives/Panel';

interface TeamMember {
  userId: number;
  email: string;
  role: 'OWNER' | 'MEMBER';
  joinedAt: Date;
}

interface TeamMembersListProps {
  members: TeamMember[];
  myUserId: number;
  canRemove: boolean;
  /** Resolves or rejects when `team.removeMember` ends. The modal stays open until then (p1). */
  onRemove: (userId: number) => Promise<unknown>;
  removeError: unknown;
  seatsLimit: number | null;
  /** Pending invites hold a seat too (`countSeatsInUse`), so the caption counts them in. */
  pendingInvitesCount: number;
  isRemoving: boolean;
}

const TeamMembersListComponent: React.FC<TeamMembersListProps> = ({
  members,
  myUserId,
  canRemove,
  onRemove,
  removeError,
  seatsLimit,
  pendingInvitesCount,
  isRemoving,
}) => {
  const intl = useIntl();
  // A member is removed only after the p1 confirmation. Remove stays disabled while the request
  // runs; the modal then closes, and an error shows in this card, under its rows (p3).
  const [confirmTarget, setConfirmTarget] = useState<TeamMember | null>(null);
  const closeConfirm = useCallback(() => setConfirmTarget(null), []);
  const confirmRemove = useCallback(() => {
    if (!confirmTarget) {
      return;
    }
    onRemove(confirmTarget.userId)
      .catch(() => undefined)
      .finally(() => setConfirmTarget(null));
  }, [confirmTarget, onRemove]);

  const rows = useMemo(
    () =>
      members.map((member) => (
        <ListGroupItem key={member.userId} className="d-flex align-items-center justify-content-between">
          <div>
            <div data-clarity-mask="true">{member.email}</div>
            <small className="text-muted">
              {member.userId === myUserId
                ? intl.formatMessage({ id: 'app.sml.team.you', defaultMessage: '(you)' })
                : intl.formatMessage(
                    { id: 'app.sml.team.joined', defaultMessage: 'Joined {date}' },
                    { date: intl.formatDate(member.joinedAt, { dateStyle: 'medium' }) }
                  )}
            </small>
          </div>
          <div className="d-flex align-items-center gap-3">
            <Badge color={member.role === 'OWNER' ? 'primary' : 'secondary'} pill>
              {member.role === 'OWNER'
                ? intl.formatMessage({ id: 'app.sml.team.role.owner', defaultMessage: 'Owner' })
                : intl.formatMessage({ id: 'app.sml.team.role.member', defaultMessage: 'Member' })}
            </Badge>
            {canRemove && member.role === 'MEMBER' && (
              <Button
                color="link"
                className="text-danger p-0"
                disabled={isRemoving}
                onClick={() => setConfirmTarget(member)}
              >
                {intl.formatMessage({ id: 'app.sml.team.remove', defaultMessage: 'Remove' })}
              </Button>
            )}
          </div>
        </ListGroupItem>
      )),
    [members, myUserId, canRemove, isRemoving, intl]
  );

  // Seats in use, the same way the server counts them (`countSeatsInUse`): members plus invites
  // that can still be accepted.
  const used = members.length + pendingInvitesCount;

  return (
    <Panel as="section" className="mb-4">
      <PanelHead
        title={intl.formatMessage({ id: 'app.sml.team.members.title', defaultMessage: 'Members' })}
        right={
          seatsLimit !== null &&
          intl.formatMessage(
            { id: 'app.sml.team.seats', defaultMessage: '{used} of {limit} seats used' },
            { used, limit: seatsLimit }
          )
        }
      />
      <PanelBody>
        <ListGroup flush>{rows}</ListGroup>
        {removeError !== null && removeError !== undefined && (
          <Alert color="danger" className="mt-3 mb-0">
            {teamErrorMessage(intl, removeError)}
          </Alert>
        )}
      </PanelBody>
      <TeamRemoveMemberModal
        member={confirmTarget}
        isRemoving={isRemoving}
        onConfirm={confirmRemove}
        onCancel={closeConfirm}
      />
    </Panel>
  );
};

export const TeamMembersList = TypedMemo(TeamMembersListComponent);
